import React, { useCallback, useState, useMemo } from 'react'
import { cloneDeep } from 'lodash'

import apiClient from '../../utils/api/apiClient'
import { APIEndpoints } from '../../utils/api/apiConfig'
import { makeCall, setStateByKey } from '../../utils/api/makeCall'
import { emptyGuid } from '../../utils/emptyGuid'

const assetTypeTemplate = {
  fieldConfiguration: '',
  name: {
    values: [],
  },
  description: {
    values: [],
  },
  systemType: '',
  isGrouped: false,
  enableOrderProcess: false,
  enableInventoryProcess: false,
  id: emptyGuid,
}

const AssetTypesContext = React.createContext({})
export const useAssetTypesState = () => {
  return React.useContext(AssetTypesContext)
}

const AssetTypesProvider = ({ children }) => {
  const [errorStates, setErrorStates] = useState({})
  const [loadingStates, setLoadingStates] = useState({})
  const [successStates, setSuccessStates] = useState({})
  const [assetTypes, setAssetTypes] = useState([])
  const [assetType, setAssetType] = useState(cloneDeep(assetTypeTemplate))
  const [redirectId, setRedirectId] = useState('')
  const [deleteAssetTypeId, setDeleteAssetTypeId] = useState('')
  const [assignedActivities, setAssignedActivities] = useState([])
  const [initialAssignedActivities, setInitialAssignedActivities] = useState(
    JSON.stringify([])
  )

  const assignedActivitiesHasChanged = useMemo(() => {
    return initialAssignedActivities !== JSON.stringify(assignedActivities)
  }, [initialAssignedActivities, assignedActivities])

  const getAssetTypes = useCallback(
    async (force) => {
      if ((assetTypes.length || loadingStates.assetTypes) && !force) {
        return null
      } else {
        await makeCall(
          'assetTypes',
          async () => {
            let loadedAssetTypes = await apiClient.getJson(
              APIEndpoints.assetTypes
            )
            setAssetTypes(loadedAssetTypes)
          },
          setErrorStates,
          setLoadingStates
        )
      }
    },
    [assetTypes.length, loadingStates.assetTypes]
  )

  async function getAssetType(assetTypeId) {
    await makeCall(
      'assetType',
      async () => {
        let loadedAssetType = await apiClient.getJson(
          APIEndpoints.administration(assetTypeId).assetType
        )
        setAssetType(loadedAssetType)
      },
      setErrorStates,
      setLoadingStates
    )
  }

  function postAssetType() {
    makeCall(
      'saveAssetType',
      async () => {
        let newAssetType = await apiClient.postJson(
          APIEndpoints.administration().assetTypes,
          assetType
        )
        setRedirectId(newAssetType.id)
        setStateByKey(setSuccessStates, 'saveAssetType', true)
        getAssetTypes(true)
      },
      setErrorStates,
      setLoadingStates
    )
  }

  function patchAssetType(assetTypeId) {
    makeCall(
      'saveAssetType',
      async () => {
        await apiClient.patchJson(
          APIEndpoints.administration(assetTypeId).assetType,
          assetType
        )
        await getAssetType(assetTypeId)
        setStateByKey(setSuccessStates, 'saveAssetType', true)
        getAssetTypes(true)
      },
      setErrorStates,
      setLoadingStates
    )
  }

  function deleteAssetType(assetTypeId) {
    makeCall(
      'deleteAssetType',
      async () => {
        await apiClient.deleteJson(
          APIEndpoints.administration(assetTypeId).assetType
        )
        await getAssetTypes(true)
        setDeleteAssetTypeId('')
        setStateByKey(setSuccessStates, 'deleteAssetType', true)
      },
      setErrorStates,
      setLoadingStates
    )
  }
  const getAssignedActivities = useCallback((assetTypeId) => {
    makeCall(
      'getAssignedActivities',
      async () => {
        let loadedAssignedActivities = await apiClient.getJson(
          APIEndpoints.administration(assetTypeId).assetTypeAssignedActivities
        )
        setAssignedActivities(loadedAssignedActivities)
        setInitialAssignedActivities(JSON.stringify(loadedAssignedActivities))
      },
      setErrorStates,
      setLoadingStates
    )
  }, [])
  async function putAssignedActivities(assetTypeId, assignedActivities) {
    await makeCall(
      'putAssignedActivities',
      async () => {
        await apiClient.putJson(
          APIEndpoints.administration(assetTypeId).assetTypeAssignedActivities,
          assignedActivities
        )
        setInitialAssignedActivities(JSON.stringify(assignedActivities))
        setStateByKey(setSuccessStates, 'putAssignedActivities', true)
      },
      setErrorStates,
      setLoadingStates
    )
  }
  const initAssignedActivities = useCallback(() => {
    setInitialAssignedActivities(JSON.stringify(assignedActivities))
    resetStates()
  }, [assignedActivities])

  const resetAssetTypesPage = useCallback(() => {
    setAssetType(cloneDeep(assetTypeTemplate))
    setErrorStates({})
    setLoadingStates({})
    setSuccessStates({})
    setRedirectId('')
  }, [])
  function resetStates() {
    setErrorStates({})
    setLoadingStates({})
    setSuccessStates({})
  }
  const contextValues = {
    errorStates,
    setErrorStates,
    loadingStates,
    setLoadingStates,
    getAssetTypes,
    assetTypes,
    assetType,
    setAssetType,
    successStates,
    setSuccessStates,
    redirectId,
    assignedActivities,
    setAssignedActivities,
    assignedActivitiesHasChanged,
    initAssignedActivities,
    getAssignedActivities,
    putAssignedActivities,
    postAssetType,
    patchAssetType,
    deleteAssetType,
    getAssetType,
    resetAssetTypesPage,
    deleteAssetTypeId,
    setDeleteAssetTypeId,
  }

  return (
    <AssetTypesContext.Provider value={contextValues}>
      {children}
    </AssetTypesContext.Provider>
  )
}

export default AssetTypesProvider
